// web/src/lib/devStore.js
// Dev-only localStorage-backed store. In Firestore mode, no JSON is bundled.

const usingFirestore = (import.meta.env.VITE_USE_FIRESTORE ?? 'true') !== 'false';

// --- Safe dynamic import helpers (only used in JSON mode) ---
async function loadJson(relPath, fallback = []) {
  try {
    const mod = await import(/* @vite-ignore */ relPath);
    return Array.isArray(mod.default) || typeof mod.default === 'object'
      ? mod.default
      : fallback;
  } catch {
    return fallback;
  }
}

// Seed data (lazy when Firestore is off)
const usersSeed        = usingFirestore ? [] : await loadJson('../json/users.json', []);
const communitiesSeed  = usingFirestore ? [] : await loadJson('../json/communities.json', []);
const groupsSeed       = usingFirestore ? [] : await loadJson('../json/groups.json', []); // optional

const KEYS = {
  users: 'dev_users',
  communities: 'dev_communities',
  groups: 'dev_groups',
  sysconfig: 'dev_sysconfig',   // system config
  docs: 'dev_docs',             // community documents (dev only)
};

// --- Default System Config (dev) ---
const DEFAULT_SYSCONFIG = {
  approvalLimits: [
    { role: 'Manager',           limit: 5000 },
    { role: 'Regional Manager',  limit: 7500 },
    { role: 'VP / Facilities',   limit: 10000 },
    { role: 'VP / Operations',   limit: 'unlimited' },
  ],
  cafNumbering: { prefix: 'CAF', seqWidth: 6 },
  uploads: { maxPdfMB: 25 },
  facilityTypes: [
    'Masonry','Elevator','Fire Hydrant / Life Safety','Roofing','HVAC','Plumbing',
    'Electrical','Parking / Paving','Façade / Windows','Landscaping / Irrigation','Security / Access',
  ],
};

// --- Normalizers ---
function normalizeUser(u) {
  const inactiveByStatus = String(u.status || 'active').toLowerCase() === 'inactive';
  const isActive = u.isActive !== undefined ? !!u.isActive : (!inactiveByStatus && !u.archivedAt);
  return { ...u, isActive };
}
function normalizeUsers(list){ return (Array.isArray(list) ? list : []).map(normalizeUser); }

// --- LS helpers ---
function readLS(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key) || 'null') ?? fallback; }
  catch { return fallback; }
}
function writeLS(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}
function ensureSeedOnce(key, seed, normalizer = x => x) {
  const existing = readLS(key, null);
  if (existing == null) writeLS(key, normalizer(seed));
}

// --- Public API (same as before) ---
export function initDevStore() {
  if (usingFirestore) {
    // Firestore mode: don’t seed or touch JSON/LS except sysconfig/docs defaults
    if (readLS(KEYS.sysconfig, null) == null) writeLS(KEYS.sysconfig, DEFAULT_SYSCONFIG);
    if (readLS(KEYS.docs, null) == null) writeLS(KEYS.docs, []);
    return;
  }
  // JSON/dev mode
  ensureSeedOnce(KEYS.users, usersSeed, normalizeUsers);
  ensureSeedOnce(KEYS.communities, communitiesSeed);
  ensureSeedOnce(KEYS.groups, groupsSeed);
  ensureSeedOnce(KEYS.sysconfig, DEFAULT_SYSCONFIG);
  if (readLS(KEYS.docs, null) == null) writeLS(KEYS.docs, []);
}

// --- Users ---
export function listUsers()        { return usingFirestore ? [] : normalizeUsers(readLS(KEYS.users, usersSeed)); }
export function saveUsers(list)    { if (!usingFirestore) writeLS(KEYS.users, normalizeUsers(list)); }

// --- Communities ---
export function listCommunities()  { return usingFirestore ? [] : readLS(KEYS.communities, communitiesSeed); }
export function saveCommunities(l) { if (!usingFirestore) writeLS(KEYS.communities, l); }

// --- Groups ---
export function listGroups()       { return usingFirestore ? [] : readLS(KEYS.groups, groupsSeed); }
export function saveGroups(l)      { if (!usingFirestore) writeLS(KEYS.groups, l); }

// --- System Config ---
export function getSysConfig() {
  const cfg = readLS(KEYS.sysconfig, DEFAULT_SYSCONFIG) || DEFAULT_SYSCONFIG;
  return {
    approvalLimits: Array.isArray(cfg.approvalLimits) ? cfg.approvalLimits : DEFAULT_SYSCONFIG.approvalLimits,
    cafNumbering: {
      prefix: (cfg.cafNumbering && cfg.cafNumbering.prefix) || DEFAULT_SYSCONFIG.cafNumbering.prefix,
      seqWidth: Number(cfg.cafNumbering && cfg.cafNumbering.seqWidth) || DEFAULT_SYSCONFIG.cafNumbering.seqWidth,
    },
    uploads: { maxPdfMB: Number(cfg.uploads && cfg.uploads.maxPdfMB) || DEFAULT_SYSCONFIG.uploads.maxPdfMB },
    facilityTypes: Array.isArray(cfg.facilityTypes) && cfg.facilityTypes.length
      ? cfg.facilityTypes
      : DEFAULT_SYSCONFIG.facilityTypes,
  };
}
export function saveSysConfig(cfg) {
  const clean = {
    approvalLimits: (cfg.approvalLimits || []).map(l => ({
      role: String(l.role || '').trim(),
      limit: (String(l.limit).toLowerCase() === 'unlimited') ? 'unlimited' : Number(l.limit || 0),
    })),
    cafNumbering: {
      prefix: String(cfg.cafNumbering?.prefix || 'CAF').trim(),
      seqWidth: Math.max(3, Number(cfg.cafNumbering?.seqWidth || 6)),
    },
    uploads: { maxPdfMB: Math.max(1, Number(cfg.uploads?.maxPdfMB || 25)) },
    facilityTypes: (Array.isArray(cfg.facilityTypes) ? cfg.facilityTypes : [])
      .map(s => String(s || '').trim())
      .filter(Boolean),
  };
  writeLS(KEYS.sysconfig, clean);
  return clean;
}

// --- Community Documents (dev-only storage) ---
export function listDocs()     { return readLS(KEYS.docs, []); }
export function saveDocs(list) { writeLS(KEYS.docs, list); }
export function upsertDoc(doc){
  const list = listDocs();
  const i = list.findIndex(d => d.id === doc.id);
  if (i >= 0) list[i] = { ...list[i], ...doc };
  else list.unshift(doc);
  saveDocs(list);
  return doc;
}
export function softDeleteDoc(id){
  const list = listDocs();
  const i = list.findIndex(d => d.id === id);
  if (i >= 0) list[i] = { ...list[i], archivedAt: new Date().toISOString() };
  saveDocs(list);
}
export function restoreDoc(id){
  const list = listDocs();
  const i = list.findIndex(d => d.id === id);
  if (i >= 0) {
    const { archivedAt, ...rest } = list[i];
    list[i] = rest;
  }
  saveDocs(list);
}

// --- IDs ---
export function makeId(prefix='id') {
  return `${prefix}_${Math.random().toString(36).slice(2,8)}${Date.now().toString(36)}`;
}